var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import Ajv from "ajv";
import uniqBy from "lodash/uniqBy";
import { ERentioFormField } from "../../components/components/Form";
import * as contactSelectors from "../../redux/contact/contact.selectors";
import { contactSelectInlineV2ValueSelector, formatContactForEdit, getValue, } from "../../redux/form/form.utils";
import { EContractMemberTypes } from "../../types/contract.types";
import { ELocale } from "../../types/i18n.types";
import { getAllRequiredCompanyFields, getAllRequiredContactFields, } from "../../utils/forms.utils";
import { getLocalizedText } from "../../utils/i18n/i18n.utils";
import { getStore } from "../../utils/redux/redux.utils";
import { EField as EContactField } from "../addContact/schema.addContact.types";
import { EField } from "./schema.createRentDeposit.types";
import { getNavigationTitle, getIsProviderBanqup, } from "./schema.createRentDeposit.utils";
var getSignersFieldProps = function (extraData) {
    return {
        "ui:field": ERentioFormField.ContactSelectInlineV2,
        title: getLocalizedText("rent_deposit.signers.label"),
        isMultiSelect: true,
        asOptionList: true,
        getRequiredContactFieldsSchema: function (contact) {
            return getAllRequiredContactFields({
                contact: contact,
                requiredFields: [
                    EContactField.Address,
                    EContactField.DateOfBirth,
                    EContactField.Nationality,
                    EContactField.PlaceOfBirth,
                ],
                appendIdentityNumberToRequiredFields: true,
            });
        },
        getRequiredCompanyFieldsSchema: function (contact) {
            return getAllRequiredCompanyFields({
                contact: contact,
                requiredFields: [
                    EContactField.Address,
                    EContactField.DateOfBirth,
                    EContactField.Nationality,
                    EContactField.PlaceOfBirth,
                    EContactField.VATNumber,
                ],
                appendIdentityNumberToRequiredFields: true,
            });
        },
        valueSelector: function (state, selectedAccountIds, formData) {
            var _a;
            if (selectedAccountIds === void 0) { selectedAccountIds = []; }
            return contactSelectInlineV2ValueSelector(state, selectedAccountIds, ((_a = extraData.contract) === null || _a === void 0 ? void 0 : _a.members.map(function (member) { return member.account; })) || []);
        },
        customGetSubtitleSelector: function (_, formData, item) {
            var accountId = item.accountId, contact = item.contact;
            var member = extraData.contract.members.find(function (member) {
                if (accountId && accountId === member.account.id) {
                    return true;
                }
                if (contact === null || contact === void 0 ? void 0 : contact.accountIds.includes(member.account.id)) {
                    return true;
                }
                return false;
            });
            if (!member) {
                return;
            }
            return member.roles
                .map(function (role) { return getLocalizedText("role.".concat(role.toLowerCase())); })
                .join(", ");
        },
        customExtraAccountsSelector: function (_, formData) {
            var contractMembers = extraData.contract.members
                .filter(function (member) {
                return member.roles.some(function (role) {
                    if (getIsProviderBanqup(formData[EField.Provider])) {
                        return [
                            EContractMemberTypes.Tenant,
                            EContractMemberTypes.Parent,
                        ].includes(role);
                    }
                    return [
                        EContractMemberTypes.Owner,
                        EContractMemberTypes.Tenant,
                        EContractMemberTypes.Parent,
                    ].includes(role);
                });
            })
                .map(function (member) { return member.account; });
            return contractMembers;
        },
        customIsFetchingSelector: function () { return false; },
        customGetContactsActions: [],
        getDisallowedAccountIds: function (_) {
            return extraData.contactMeMaster && extraData.isBroker
                ? [extraData.contactMeMaster.account.id]
                : [];
        },
    };
};
export var getSchema = function (initialValues, extraData) {
    return function (defaultValues) {
        var _a;
        var providerIsBanqup = getIsProviderBanqup(defaultValues[EField.Provider]);
        return {
            required: providerIsBanqup ? [EField.TenantSigners] : [EField.Signers],
            properties: (_a = {},
                _a[EField.Header + "2"] = {
                    default: undefined,
                },
                _a[EField.LegalContactInfo] = {
                    default: undefined,
                    // @ts-ignore
                    showIf: function (formData) {
                        return extraData.isBroker && !extraData.legalContact;
                    },
                },
                _a[EField.Signers] = {
                    default: getValue(EField.Signers, initialValues, defaultValues),
                    // @ts-ignore
                    showIf: function (formData) { return !providerIsBanqup; },
                },
                _a[EField.TenantSigners] = {
                    default: getValue(EField.TenantSigners, initialValues, defaultValues),
                    // @ts-ignore
                    showIf: function (formData) { return providerIsBanqup; },
                },
                _a[EField.SignAsBroker] = {
                    default: getValue(EField.SignAsBroker, initialValues, defaultValues),
                    // @ts-ignore
                    showIf: function (formData) {
                        return !providerIsBanqup &&
                            extraData.isBroker &&
                            extraData.legalContact &&
                            extraData.contactMeMaster;
                    },
                    options: (function () {
                        if (!extraData.contactMeMaster) {
                            return [];
                        }
                        return [
                            {
                                id: "".concat(extraData.contactMeMaster.account.id),
                                value: extraData.contactMeMaster.account.company ||
                                    "".concat(extraData.contactMeMaster.account.firstname, " ").concat(extraData.contactMeMaster.account.lastname),
                                subValue: getLocalizedText("role.broker"),
                            },
                        ];
                    })(),
                },
                _a[EField.Locale] = {
                    default: getValue(EField.Locale, initialValues, defaultValues),
                    // @ts-ignore
                    options: [ELocale.NL, ELocale.FR].map(function (locale) { return ({
                        id: locale,
                        value: getLocalizedText("system.locale.".concat(locale)),
                    }); }),
                },
                _a),
        };
    };
};
export var uiSchema = function (extraData) {
    var _a;
    return (_a = {
            "rentio:title": getLocalizedText("rent_deposit.signers.heading.summary"),
            "rentio:navigationTitle": getNavigationTitle(extraData)
        },
        _a[EField.Header + "2"] = {
            "ui:field": ERentioFormField.SectionHeader,
            title: getLocalizedText("rent_deposit.signers.heading"),
        },
        _a[EField.LegalContactInfo] = {
            "ui:field": ERentioFormField.HelpInfo,
            type: "warning",
            title: getLocalizedText("document_package.form.no_legal_contact_info.heading"),
            subtitle: getLocalizedText("document_package.form.no_legal_contact_info.content"),
        },
        _a[EField.Signers] = getSignersFieldProps(extraData),
        _a[EField.TenantSigners] = getSignersFieldProps(extraData),
        _a[EField.SignAsBroker] = {
            "ui:field": ERentioFormField.InlineSelect,
            title: getLocalizedText("document_package.form.sign_as_broker.label"),
            isMultiSelect: true,
            asOptionList: true,
            hideOnSummaryIfEmpty: true,
            formatSummary: function (formData) {
                if (!formData || !extraData.contactMeMaster) {
                    return;
                }
                return (extraData.contactMeMaster.account.company ||
                    "".concat(extraData.contactMeMaster.account.firstname, " ").concat(extraData.contactMeMaster.account.lastname));
            },
        },
        _a[EField.Locale] = {
            "ui:field": ERentioFormField.DropdownSelect,
            title: getLocalizedText("document_package.form.locale.title"),
            formatSummary: function (formData) {
                return getLocalizedText("system.locale.".concat(formData || ELocale.NL));
            },
        },
        _a);
};
export var getValidate = function (extraData) { return function (formData, errors) {
    var hasTenantSignersField = Object.keys(formData).includes(EField.TenantSigners);
    if (hasTenantSignersField) {
        var tenants = extraData.contract.members.filter(function (member) {
            return member.roles.some(function (role) {
                return [EContractMemberTypes.Tenant, EContractMemberTypes.Parent].includes(role);
            });
        });
        if (tenants.length !== formData[EField.TenantSigners].length &&
            errors[EField.TenantSigners]) {
            errors[EField.TenantSigners].addError(getLocalizedText("rent_deposit.signers.error.all_tenants.required"));
        }
        return errors;
    }
    var hasSignAsBroker = formData[EField.SignAsBroker] && formData[EField.SignAsBroker].length > 0;
    if (!formData[EField.Signers] && !hasSignAsBroker) {
        errors[EField.Signers].addError(getLocalizedText("rent_deposit.form.signers.error.required"));
    }
    var signers = uniqBy(extraData.contract.members.filter(function (member) {
        return (formData[EField.Signers] || []).includes(member.account.id);
    }), "account.id");
    // Validate if all owners have complete info when broker is selected as signer
    if (hasSignAsBroker) {
        var ajv_1 = new Ajv({ allErrors: true, jsonPointers: true });
        extraData.contract.members.forEach(function (member) {
            if (member.roles.includes(EContractMemberTypes.Owner)) {
                var store = getStore();
                if (!store) {
                    return;
                }
                var contactForMember = contactSelectors.getContact(store.getState(), undefined, member.account.id);
                if (!contactForMember) {
                    return;
                }
                var validateContact = ajv_1.compile(
                // @ts-ignore
                uiSchema(extraData)[EField.Signers].getRequiredContactFieldsSchema(contactForMember));
                var validateCompany = ajv_1.compile(
                // @ts-ignore
                uiSchema(extraData)[EField.Signers].getRequiredCompanyFieldsSchema(contactForMember));
                var validate = !!contactForMember.company
                    ? validateCompany
                    : validateContact;
                if (!validate(formatContactForEdit(contactForMember))) {
                    errors[EField.Signers].addError(getLocalizedText("rent_deposit.form.signers.owner_extra_info.error.required"));
                }
            }
        });
    }
    var allPickedRoles = signers.reduce(function (acc, member) {
        return member.roles ? __spreadArray(__spreadArray([], acc, true), member.roles, true) : __spreadArray(__spreadArray([], acc, true), [], false);
    }, []);
    var hasTenant = allPickedRoles.includes(EContractMemberTypes.Tenant) ||
        allPickedRoles.includes(EContractMemberTypes.Parent);
    var hasOwner = allPickedRoles.includes(EContractMemberTypes.Owner) || hasSignAsBroker;
    if (!hasTenant || !hasOwner) {
        errors[EField.Signers].addError(getLocalizedText("rent_deposit.signers.error.owner_and_tenant.required"));
    }
    return errors;
}; };
